import Image from '@base/Image';
import styled from '@emotion/styled';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import CheckContained from 'aquilla/icons/misc/CheckContained';
import useCertificationCourseData from './useCertificationCourseData';

const Container = styled.div`
  display: grid;
  width: 100%;
  row-gap: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    row-gap: ${Spacings.SPACING_8B};
  }
`;

const Title = styled(Typography)``;

const Description = styled(Typography)`
  ${mediaQuery.uptoMobile} {
    width: 100%;
    line-height: 18px;
    margin-top: -${Spacings.SPACING_4B};
  }
`;

const SectionWrapper = styled.div`
  display: grid;
  gap: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_2B};
  }
`;
const SectionSubWrapper = styled.div`
  display: grid;
  grid-gap: ${Spacings.SPACING_4B};
  grid-template-columns: ${({ $columnsTemplate }) => {
    return $columnsTemplate;
  }};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    grid-template-columns: 1fr;
    gap: ${Spacings.SPACING_2B};
  }
`;

const SectionPoints = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_2B};
`;

const SectionTitle = styled(Typography)``;

const SectionPoint = styled(Typography)`
  font-weight: ${Fonts.LIGHT};
`;
const SectionDescription = styled(Typography)`
  font-weight: ${Fonts.LIGHT};
`;

const Section = ({ points, columnsTemplate }) => {
  return (
    <SectionWrapper>
      <SectionTitle variant="h5">{points?.label}</SectionTitle>
      <SectionSubWrapper $columnsTemplate={columnsTemplate}>
        {points?.items?.map((point) => {
          return (
            <SectionPoints key={point}>
              <CheckContained
                height={20}
                width={20}
                color="var(--color-text-primary)"
              />
              <SectionPoint variant="h6" color="var(--color-text-primary)">
                {point}
              </SectionPoint>
            </SectionPoints>
          );
        })}
      </SectionSubWrapper>
    </SectionWrapper>
  );
};

const TextBlockWrapper = styled.div`
  display: grid;
  gap: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    row-gap: ${Spacings.SPACING_2B};
  }
`;

const TextBlock = ({ block }) => {
  return (
    <TextBlockWrapper>
      <SectionTitle variant="h5">{block.label}</SectionTitle>
      <SectionDescription variant="h6" color="var(--color-text-primary)">
        {block.description}
      </SectionDescription>
    </TextBlockWrapper>
  );
};

const ToolsWrapper = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_4B};
  flex-wrap: wrap;
  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_3B};
  }
`;

const ImageWrapper = styled.div`
  min-width: 99px;
  height: 28px;
  position: relative;
`;
const ToolWrapper = styled.div`
  background: #ffffff;
  border: 1px solid #dde6ed;
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_2B};
  }
`;

const ToolsCovered = ({ block }) => {
  return (
    <TextBlockWrapper>
      <SectionTitle variant="h5">{block.label}</SectionTitle>
      <ToolsWrapper>
        {block?.items?.map((item) => {
          return (
            <ToolWrapper key={item.id}>
              <ImageWrapper>
                <Image src={item.imageSrc} layout="fill" alt="tools-covered" />
              </ImageWrapper>
            </ToolWrapper>
          );
        })}
      </ToolsWrapper>
    </TextBlockWrapper>
  );
};

const Overview = ({ goalUID }) => {
  const { sections } = useCertificationCourseData(goalUID);
  const overview = sections?.overview ?? {};
  return (
    <Container>
      <Title variant="h3">Overview</Title>
      <Description variant="p2" color="var(--color-text-primary)">
        {overview.programmeOverviewDescription}
      </Description>
      <Section
        points={overview.programmeKeyhighlights}
        columnsTemplate="1fr 1fr"
      />
      <TextBlock block={overview.programmeTargetAudience} />
      <Section points={overview.programmeEligibility} columnsTemplate="1fr" />
      <ToolsCovered block={overview.programmeToolsCovered} />
    </Container>
  );
};

export default Overview;
